/**
 * Studio Registry Service - Servicio para interactuar con la API de Studios
 */

import {
  StudioModule,
  StudioCreateRequest,
  StudioUpdateRequest,
  StudioGenerateRequest,
  StudioListResponse,
  StudioGenerateResponse,
  StudioErrorResponse,
  StudioDefinitionSummary,
  StudioResultsResponse
} from '../types/studio';
import { authManager } from '../utils/authManager';
import logger from '../utils/simpleLogger';

/**
 * Clase para manejar todas las operaciones de Studios con la API
 */
class StudioRegistryService {
  private baseUrl: string;
  private v2BaseUrl: string;
  private token: string | null = null;

  constructor() {
    // Configurar baseUrl igual que apiService
    const envBaseUrl = import.meta.env.VITE_API_BASE_URL;
    const defaultBaseUrl = import.meta.env.DEV
      ? 'http://localhost:8001/api'
      : '/api';

    // Normalizar para evitar doble barra final
    const normalizedBaseUrl = (envBaseUrl || defaultBaseUrl).replace(/\/$/, '');
    this.baseUrl = normalizedBaseUrl;
    this.v2BaseUrl = `${normalizedBaseUrl}/v2/studios`;

    // Obtener token del localStorage usando el mismo nombre que apiService
    this.token = localStorage.getItem('access_token');
  }

  /**
   * Actualiza el token de autenticación
   */
  setAuthToken(token: string | null) {
    this.token = token;
    if (token) {
      localStorage.setItem('access_token', token);
    } else {
      localStorage.removeItem('access_token');
    }
  }

  /**
   * Obtiene headers comunes para las requests
   * Usa authManager para obtener siempre el token más reciente
   */
  private getHeaders(): HeadersInit {
    return authManager.getAuthHeaders();
  }

  /**
   * Maneja errores de la API de manera consistente
   */
  private async handleResponse<T>(response: Response): Promise<T> {
    if (!response.ok) {
      let errorMessage = `HTTP ${response.status}: ${response.statusText}`;

      try {
        const errorData = await response.json();
        logger.error('❌ [Studio Registry] Error response:', errorData);

        if (errorData.error) {
          // Si error es un objeto, extraer el mensaje
          if (typeof errorData.error === 'object') {
            errorMessage = errorData.error.message || errorData.error.code || JSON.stringify(errorData.error);
          } else {
            errorMessage = errorData.error;
          }
        } else if (errorData.message) {
          errorMessage = errorData.message;
        }
      } catch (e) {
        // Si no se puede parsear el error, usar el mensaje por defecto
        logger.error('❌ [Studio Registry] Could not parse error response:', e);
      }

      throw new Error(errorMessage);
    }

    return response.json();
  }

  /**
   * Obtiene todos los Studios disponibles para el usuario autenticado
   */
  async getStudios(): Promise<StudioModule[]> {
    logger.info('📡 GET /api/v2/studios - Obteniendo Studios...');

    try {
      const response = await fetch(`${this.v2BaseUrl}`, {
        method: 'GET',
        headers: this.getHeaders(),
      });

      const data: StudioListResponse = await this.handleResponse(response);

      if (data.success) {
        const studios = data.studios.map(def => this.mapDefinitionToModule(def));
        logger.info(`✅ Studios obtenidos: ${data.meta.count} Studios`);
        return studios;
      } else {
        throw new Error('Error en respuesta del servidor');
      }
    } catch (error) {
      console.error('❌ Error obteniendo Studios:', error);
      throw error;
    }
  }

  /**
   * Genera contenido para un Studio específico
   */
  async generateContent(
    studioId: string,
    request: StudioGenerateRequest
  ): Promise<StudioGenerateResponse> {
    logger.info(`📡 POST /api/v2/studios/${studioId}/execute - Generando contenido...`);
    logger.info(`   - Studio: ${studioId}`);
    logger.info(`   - Documentos: ${request.document_ids.length}`);
    logger.info(`   - Force regenerate: ${request.force_regenerate || false}`);

    try {
      const payload = {
        document_ids: request.document_ids,
        force: request.force_regenerate ?? false,
        overrides: request.overrides ?? {},
        metadata: request.metadata ?? {},
      };

      const response = await fetch(`${this.v2BaseUrl}/${studioId}/execute`, {
        method: 'POST',
        headers: this.getHeaders(),
        body: JSON.stringify(payload),
      });

      const data: StudioGenerateResponse | StudioErrorResponse = await this.handleResponse(response);

      if (data.success) {
        logger.info(`✅ Contenido generado para Studio '${studioId}'`);
        logger.info(`   - Cached: ${data.cached}`);
        return data as StudioGenerateResponse;
      } else {
        const errorData = data as StudioErrorResponse;
        throw new Error(errorData.error || 'Error generando contenido');
      }
    } catch (error) {
      console.error(`❌ Error generando contenido para Studio '${studioId}':`, error);
      throw error;
    }
  }

  /**
   * Crea un nuevo Studio personalizado
   */
  async createStudio(request: StudioCreateRequest): Promise<StudioModule> {
    logger.info('📡 POST /api/v2/studios - Creando Studio personalizado...');

    const payload = {
      title: request.name,
      description: request.description ?? '',
      icon: request.icon ?? request.ui_config?.icon ?? 'Sparkles',
      visibility: 'private',
      prompt_template: request.prompt_template ?? {
        system_message: '',
        user_prompt: '',
        rag_query: ''
      },
      model_settings: request.model_params ?? {
        model: 'gpt-4o-mini',
        temperature: 0.3,
        max_tokens: 1500
      },
      rag_profile: {},
      tags: [],
      extends: request.extends
    };

    const response = await fetch(`${this.v2BaseUrl}`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify(payload)
    });

    const data = await this.handleResponse<{ success: boolean; studio: StudioDefinitionSummary }>(response);

    if (!data.success || !data.studio) {
      throw new Error('Error creando el Studio');
    }

    logger.info(`✅ Studio creado: ${data.studio.id}`);
    return this.mapDefinitionToModule(data.studio);
  }

  /**
   * Actualiza un Studio existente
   */
  async updateStudio(
    studioId: string,
    request: StudioUpdateRequest
  ): Promise<StudioModule> {
    throw new Error('La edición de Studios aún no está disponible en Studio v2.');
  }

  /**
   * Elimina un Studio personalizado
   */
  async deleteStudio(studioId: string): Promise<void> {
    throw new Error('La eliminación de Studios aún no está disponible en Studio v2.');
  }

  /**
   * Limpia el caché de un Studio específico
   */
  async clearCache(studioId: string): Promise<void> {
    logger.info(`📡 DELETE /api/v2/studios/${studioId}/cache - Limpiando caché...`);

    const response = await fetch(`${this.v2BaseUrl}/${studioId}/cache`, {
      method: 'DELETE',
      headers: this.getHeaders(),
    });

    const data = await this.handleResponse(response) as any;
    if (data.success) {
      logger.info(`✅ Caché del Studio '${studioId}' limpiado (entradas purgadas: ${data.purged})`);
    } else {
      throw new Error(data.error || 'Error limpiando caché');
    }
  }

  /**
   * Reordena múltiples Studios
   */
  async reorderStudios(studios: StudioModule[]): Promise<void> {
    console.warn('⚠️ Reordenar Studios no está disponible en Studio v2 aún.');
  }

  /**
   * Obtiene un Studio específico por ID
   */
  async getStudio(studioId: string): Promise<StudioModule | null> {
    logger.info(`📡 GET /api/v2/studios/${studioId} - Obteniendo Studio...`);

    const response = await fetch(`${this.v2BaseUrl}/${studioId}`, {
      method: 'GET',
      headers: this.getHeaders(),
    });

    const data = await this.handleResponse(response) as any;
    if (data.success && data.studio) {
      return this.mapDefinitionToModule(data.studio);
    }
    return null;
  }

  /**
   * Verifica si el usuario tiene permisos para editar un Studio
   */
  canEditStudio(studio: StudioModule): boolean {
    // Los Studios globales no se pueden editar directamente
    // Solo se pueden crear copias personalizadas
    return studio.visibility === 'user' || studio.is_custom === true;
  }

  /**
   * Obtiene el ícono apropiado para un Studio
   */
  getStudioIcon(studio: StudioModule): string {
    return studio.icon || 'Settings';
  }

  /**
   * Obtiene el nombre para mostrar de un Studio
   */
  getStudioDisplayName(studio: StudioModule): string {
    return studio.name || studio.id;
  }

  /**
   * Formatea el tiempo de procesamiento en un formato legible
   */
  formatProcessingTime(timeMs: number): string {
    if (timeMs < 1000) {
      return `${timeMs}ms`;
    } else if (timeMs < 60000) {
      return `${(timeMs / 1000).toFixed(1)}s`;
    } else {
      return `${Math.floor(timeMs / 60000)}m ${Math.floor((timeMs % 60000) / 1000)}s`;
    }
  }

  /**
   * Obtiene todos los resultados guardados para un proyecto desde la BD
   */
  async getResults(): Promise<Record<string, any>> {
    logger.info(`📡 GET /api/v2/studios/results - Obteniendo resultados...`);

    const response = await fetch(`${this.v2BaseUrl}/results`, {
      method: 'GET',
      headers: this.getHeaders(),
    });

    const data: StudioResultsResponse = await this.handleResponse(response);
    if (!data.success) {
      throw new Error('Error obteniendo resultados');
    }

    const results: Record<string, any> = {};
    data.results.forEach(result => {
      results[result.studio_id] = result.assistant_message;
    });
    return results;
  }

  private mapDefinitionToModule(definition: StudioDefinitionSummary): StudioModule {
    const promptTemplate = definition.prompt_template ?? {
      system_message: '',
      user_prompt: '{context}',
      rag_query: definition.description || `Analiza los documentos seleccionados para ${definition.title}`,
    };

    const modelParams = {
      model: definition.model_settings?.model ?? 'gpt-4o-mini',
      temperature: definition.model_settings?.temperature ?? 0.3,
      max_tokens: definition.model_settings?.max_tokens ?? 2000,
    };

    // Todos los Studios usan generator_key: 'generic' (simplificado)
    // La especialización se maneja mediante prompts personalizados en la configuración
    const generatorKey = 'generic';

    return {
      id: definition.id,
      name: definition.title,
      description: definition.description ?? '',
      icon: definition.icon ?? 'Settings',
      generator_key: generatorKey,
      prompt_template: promptTemplate,
      model_params: modelParams,
      extends: undefined,
      ui_config: {
        show_generate_button: true,
        layout: 'default',
        icon: definition.icon ?? 'Settings',
      },
      visibility: definition.visibility === 'private' ? 'user' : definition.visibility === 'project' ? 'project' : 'global',
      created_at: definition.created_at,
      updated_at: definition.updated_at,
      order: definition.display_order,
      is_custom: Boolean(definition.owner_id),
    };
  }
}

// Instancia singleton del servicio
export const studioRegistry = new StudioRegistryService();

// Exportar también la clase para casos especiales
export default StudioRegistryService;
